import React from "react";
// ANT DESIGN COMPONENTS
import { Drawer, Switch, Radio } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { faElevator, faListCheck, faServer } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  updateOpenMonitoringDeviceConfigurationDrawer,
  updateMonitoringDeviceConfiguration,
} from "../../../redux/monitoring/monitoringSlice";
// COMPONENTS
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import ButtonClose from "../../components/buttons/ButtonClose";
// INTERFACES
import { RootState } from "../../../app/store";
import { MonitoringDeviceConfigurationInterface } from "../../../interfaces/Monitoring.interface";
import { MessageTypeInterface } from "../../../interfaces/MessageType.interface";

export default function MonitoringDeviceConfigurationsDrawer({
  messagesTypes,
  setDeviceMessageTypes,
}: {
  messagesTypes: MessageTypeInterface[];
  setDeviceMessageTypes: (copy: MessageTypeInterface[]) => void;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { openMonitoringDeviceConfigurationDrawer, monitoringDeviceConfiguration } = useSelector(
    (state: RootState) => state.monitoring,
  );
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onClickClose = () => {
    dispatch(updateOpenMonitoringDeviceConfigurationDrawer(false));
  };

  const onClickUnitFilter = (
    unitName: "elevator" | "escalator" | "movingwalk" | "gpio",
    newValue: boolean,
  ) => {
    const copy: MonitoringDeviceConfigurationInterface = structuredClone(
      monitoringDeviceConfiguration,
    );
    if (unitName === "elevator") {
      copy.showElevators = newValue;
    } else if (unitName === "escalator") {
      copy.showEscalators = newValue;
    } else if (unitName === "movingwalk") {
      copy.showMovingwalks = newValue;
    } else if (unitName === "gpio") {
      copy.showGpio = newValue;
    }
    dispatch(updateMonitoringDeviceConfiguration(copy));
  };

  const onClickViewType = (newValue: "server" | "bank") => {
    const copy: MonitoringDeviceConfigurationInterface = structuredClone(
      monitoringDeviceConfiguration,
    );
    copy.viewType = newValue;
    dispatch(updateMonitoringDeviceConfiguration(copy));
  };

  const onChangeMessageTypeSwitch = (newValue: boolean, message: MessageTypeInterface) => {
    const copy: MessageTypeInterface[] = structuredClone(messagesTypes);
    const index = copy.findIndex((object: MessageTypeInterface) => object.id === message.id);
    if (index >= 0) {
      copy[index].active = newValue;
    }
    setDeviceMessageTypes(copy);
  };

  // ************************************************ */
  // USE EFFECT ************************************* */

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Drawer
      width="550"
      placement="right"
      onClose={onClickClose}
      closable={false}
      open={openMonitoringDeviceConfigurationDrawer}
      footer={
        <div className="container generalStyles__flex mt-4">
          <div className="generalStyles__width100 generalStyles__mrFix">
            <ButtonClose onClick={onClickClose} />
          </div>
        </div>
      }
    >
      <DrawerRowDivider icon={faServer} title={t("general.viewType")} />
      <div className={`drawer__box__${theme}`}>
        {/* FILTER BY UNIT ELEVATOR */}
        <div className="generalStyles__flex">
          <div className="generalStyles__inputFlexNameLarge">
            <span className="generalStyles__inputFlexRequired">*</span>
            <span className="drawer__title">{t("general.viewType")}</span>
          </div>
          <div className="drawer__textContainer generalStyles__width100">
            <Radio.Group
              value={monitoringDeviceConfiguration.viewType}
              onChange={(e) => {
                onClickViewType(e.target.value);
              }}
            >
              <Radio value="server">{t("general.servers")}</Radio>
              <Radio value="bank">{t("general.banks")}</Radio>
            </Radio.Group>
          </div>
        </div>
      </div>
      <br />
      <DrawerRowDivider icon={faElevator} title={t("general.units")} />
      <div className={`drawer__box__${theme}`}>
        {/* FILTER BY UNIT ELEVATOR */}
        <div className="generalStyles__flex">
          <div className="generalStyles__inputFlexNameLarge">
            <span className="generalStyles__inputFlexRequired">*</span>
            <span className="drawer__title">Show Elevators</span>
          </div>
          <div className="drawer__textContainer generalStyles__width100">
            <Switch
              checked={monitoringDeviceConfiguration.showElevators}
              onChange={(checked: boolean) => {
                onClickUnitFilter("elevator", checked);
              }}
            />
          </div>
        </div>
        {/* FILTER BY UNIT ELEVATOR */}
        <div className="generalStyles__flex mt-3">
          <div className="generalStyles__inputFlexNameLarge">
            <span className="generalStyles__inputFlexRequired">*</span>
            <span className="drawer__title">Show Escalators</span>
          </div>
          <div className="drawer__textContainer generalStyles__width100">
            <Switch
              checked={monitoringDeviceConfiguration.showEscalators}
              onChange={(checked: boolean) => {
                onClickUnitFilter("escalator", checked);
              }}
            />
          </div>
        </div>
        {/* FILTER BY UNIT ELEVATOR */}
        <div className="generalStyles__flex mt-3">
          <div className="generalStyles__inputFlexNameLarge">
            <span className="generalStyles__inputFlexRequired">*</span>
            <span className="drawer__title">Show Movingwalks</span>
          </div>
          <div className="drawer__textContainer generalStyles__width100">
            <Switch
              checked={monitoringDeviceConfiguration.showMovingwalks}
              onChange={(checked: boolean) => {
                onClickUnitFilter("movingwalk", checked);
              }}
            />
          </div>
        </div>
        {/* FILTER BY UNIT ELEVATOR */}
        <div className="generalStyles__flex mt-3">
          <div className="generalStyles__inputFlexNameLarge">
            <span className="generalStyles__inputFlexRequired">*</span>
            <span className="drawer__title">Show GPIO</span>
          </div>
          <div className="drawer__textContainer generalStyles__width100">
            <Switch
              checked={monitoringDeviceConfiguration.showGpio}
              onChange={(checked: boolean) => {
                onClickUnitFilter("gpio", checked);
              }}
            />
          </div>
        </div>
      </div>
      <br />
      <DrawerRowDivider icon={faListCheck} title={t("general.messageTypes")} />
      <div className={`drawer__box__${theme}`}>
        {messagesTypes.map((message: MessageTypeInterface) => (
          <div key={message.id} className="generalStyles__flex mt-3">
            <div className="generalStyles__inputFlexNameLarge generalStyles__textEllipsis">
              <span className="generalStyles__inputFlexRequired">*</span>
              <span className="drawer__title ">{message.name}</span>
            </div>
            <div className="drawer__textContainer generalStyles__width100">
              <Switch
                checked={message.active}
                onChange={(checked: boolean) => {
                  onChangeMessageTypeSwitch(checked, message);
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </Drawer>
  );
}
